"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.popularExerciseTemplateIds = exports.filterExercises = exports.newFilterExercises = exports.createFuseInstance = void 0;
const fuse_js_1 = __importDefault(require("fuse.js"));
const utils_1 = require("./utils");
const lodash_1 = require("lodash");
const createFuseInstance = (exercises, language) => {
    const options = {
        keys: [
            // Higher weight is greater value (more important)
            { name: 'title', weight: 5 },
            { name: `${language.toLowerCase()}_title`, weight: 5 },
            { name: 'aka', weight: 1 },
            { name: 'equipment_category', weight: 3 },
            { name: 'muscle_group', weight: 8 },
            { name: 'localized_muscle_group_name', weight: 8 },
        ],
        //Determines approximately where in the text is the pattern expected to be found.
        location: undefined,
        // Determines how close the match must be to the fuzzy location (specified by location).
        // An exact letter match which is distance characters away from the fuzzy location would
        // score as a complete mismatch. A distance of 0 requires the match be at the exact location specified.
        // A distance of 1000 would require a perfect match to be within 800 characters of the location to be found using a threshold of 0.8.
        distance: 3,
        // At what point does the match algorithm give up. A threshold of 0.0 requires a perfect match
        // (of both letters and location), a threshold of 1.0 would match anything.
        threshold: 0.3,
        // Only the matches whose length exceeds this value will be returned.
        // (For instance, if you want to ignore single character matches in the result, set it to 2).
        minMatchCharLength: 1,
        isCaseSensitive: false,
        includeScore: true,
        shouldSort: true,
        // Whether the matches should be included in the result set. When true, each record in
        // the result set will include the indices of the matched characters. These can consequently be used for highlighting purposes.
        includeMatches: true,
        // When true, the matching function will continue to the end of a search pattern
        // even if a perfect match has already been located in the string.
        findAllMatches: false,
        // When true, it enables the use of unix-like search commands. See example.
        useExtendedSearch: true,
        // When true, search will ignore location and distance, so it won't matter
        // where in the string the pattern appears.
        ignoreLocation: true,
        ignoreFieldNorm: false, // this is false to bring back dumbbell row for dumbbell back
        fieldNormWeight: 0.5,
    };
    return new fuse_js_1.default(exercises, options);
};
exports.createFuseInstance = createFuseInstance;
const newFilterExercises = ({ exercises, muscleGroupFilter, equipmentType, searchText, language, fuseInstance, }) => {
    let result = exercises;
    result = filterExercisesWithMuscleGroup(muscleGroupFilter, result);
    result = filterExercisesWithEquipment(equipmentType, result);
    const fuseQuery = createFuseQuery({ searchText, language });
    result = newFilterExercisesWithSearchText(fuseQuery, result, language, fuseInstance);
    return result;
};
exports.newFilterExercises = newFilterExercises;
const filterExercises = ({ exercises, muscleGroupFilter, equipmentType, searchText, language, }) => {
    let result = exercises;
    result = filterExercisesWithMuscleGroup(muscleGroupFilter, result);
    result = filterExercisesWithEquipment(equipmentType, result);
    const normalizedSearchText = normalizeSearchText(searchText, language);
    result = filterExercisesWithSearchText(normalizedSearchText, result, language);
    result = orderExercisesWithSearchText(normalizedSearchText, result);
    return result;
};
exports.filterExercises = filterExercises;
const filterExercisesWithMuscleGroup = (muscleGroup, exercises) => {
    if (muscleGroup === 'all_muscles') {
        return exercises;
    }
    return exercises.filter((rd) => rd.muscle_group === muscleGroup);
};
const filterExercisesWithEquipment = (equipmentType, exercises) => {
    if (equipmentType === 'all_equipment') {
        return exercises;
    }
    return exercises.filter((rd) => rd.equipment_category === equipmentType);
};
const newFilterExercisesWithSearchText = (searchText, exercises, language, fuseInstance) => {
    if (searchText.length === 0) {
        exercises.forEach((e) => (Object.assign(Object.assign({}, e), { searchStringScore: 1 })));
        return exercises;
    }
    const customExercises = exercises.filter((e) => e.is_custom);
    if (searchText.toLowerCase() === 'custom') {
        customExercises.forEach((e) => (Object.assign(Object.assign({}, e), { searchStringScore: 0 })));
        return customExercises;
    }
    const fuseQuery = createFuseQuery({ searchText, language });
    const fuseResults = fuseInstance.search(fuseQuery);
    // Setting search scores for weights in ExerciseSearchDelegate
    const results = fuseResults.map((r) => {
        var _a;
        return (Object.assign(Object.assign({}, r.item), { searchStringScore: r.score === 0 ? 0 : -Math.log10((_a = r.score) !== null && _a !== void 0 ? _a : 1) / 100 }));
    });
    return results;
};
// Old filter exercise functionality, will be removed when new feature goes live
const filterExercisesWithSearchText = (searchText, exercises, language) => {
    if (searchText.length === 0) {
        return exercises;
    }
    return exercises.filter((e) => {
        const searchableSearchText = searchable(searchText);
        const searchableEnglishTitle = searchable(e.title);
        const searchableLocalizedTitleMap = !e.is_custom
            ? {
                en: searchable(e.title),
                es: e.es_title ? searchable(e.es_title) : undefined,
                de: e.de_title ? searchable(e.de_title) : undefined,
                fr: e.fr_title ? searchable(e.fr_title) : undefined,
                it: e.it_title ? searchable(e.it_title) : undefined,
                pt: e.pt_title ? searchable(e.pt_title) : undefined,
                tr: e.tr_title ? searchable(e.tr_title) : undefined,
                zh_CN: e.zh_cn_title ? searchable(e.zh_cn_title) : undefined,
                zh_TW: e.zh_tw_title ? searchable(e.zh_tw_title) : undefined,
                ru: e.ru_title ? searchable(e.ru_title) : undefined,
                ja: e.ja_title ? searchable(e.ja_title) : undefined,
                ko: e.ko_title ? searchable(e.ko_title) : undefined,
            }
            : {
                en: searchable(e.title),
            };
        const searchableLocalizedTitle = searchableLocalizedTitleMap[language] || searchableLocalizedTitleMap.en;
        const searchableLocalizedMuscleGroupName = searchable(e.localized_muscle_group_name);
        const searchableMuscleGroup = searchable(e.muscle_group);
        const searchableMisspelledDumbbellEquipment = e.equipment_category === 'dumbbell' ? 'dumbell' : '';
        const searchableTags = !e.is_custom && e.manual_tag ? searchable(e.manual_tag) : '';
        const searchableAka = !e.is_custom && e.aka ? searchable(e.aka) : '';
        if (searchableLocalizedTitle.includes(searchableSearchText) ||
            searchableEnglishTitle.includes(searchableSearchText) ||
            searchableLocalizedMuscleGroupName.includes(searchableSearchText) ||
            searchableMuscleGroup.includes(searchableSearchText)) {
            return true;
        }
        if (e.is_custom && 'custom'.includes(searchableSearchText)) {
            return true;
        }
        const splitSearch = searchableSearchText.split(' ');
        const splitTitle = [
            searchableLocalizedTitle,
            searchableEnglishTitle,
            searchableTags,
            searchableAka,
            searchableMisspelledDumbbellEquipment,
        ]
            .join(' ')
            .split(' ');
        // Find:
        // title ['arnold', 'press', '(barbell)']
        // search ['press', 'bar']
        if ((0, lodash_1.intersectionWith)(splitSearch, splitTitle, (search, title) => title.includes(search)).length === splitSearch.length) {
            return true;
        }
        return false;
    });
};
const orderExercisesWithSearchText = (searchText, exercises) => {
    if (searchText.length === 0) {
        return exercises;
    }
    return exercises
        .reduce((accu, currentExercise) => {
        const lowercaseTitle = currentExercise.title.toLowerCase();
        if (lowercaseTitle.startsWith(searchText)) {
            accu.push({
                exercise: currentExercise,
                weight: currentExercise.priority * 2,
            });
            return accu;
        }
        accu.push({
            exercise: currentExercise,
            weight: currentExercise.priority,
        });
        return accu;
    }, [])
        .sort((a, b) => b.weight - a.weight)
        .map((a) => a.exercise);
};
/**
 * trims, lowercases and removes trailing "s" character to
 * simplify english searches
 */
const normalizeSearchText = (searchText, language) => {
    const searchableSearchText = searchable(searchText).replace('-', ' ').trim();
    if (language !== 'en')
        return searchableSearchText;
    if (searchableSearchText.length < 3)
        return searchableSearchText;
    const lastCharacter = searchableSearchText.slice(-1);
    if (lastCharacter !== 's')
        return searchableSearchText;
    return searchableSearchText.slice(0, -1);
};
const createFuseQuery = ({ searchText, language, }) => {
    const fuseQueryText = searchable(searchText)
        .trim()
        .replace('-', ' ')
        .replace(' ', '|');
    if (language !== 'en')
        return fuseQueryText;
    if (fuseQueryText.length < 3)
        return fuseQueryText;
    const lastCharacter = fuseQueryText.slice(-1);
    if (lastCharacter !== 's')
        return fuseQueryText;
    return fuseQueryText.slice(0, -1);
};
const searchable = (str) => {
    return (0, utils_1.removeAccents)(str.toLowerCase());
};
/**
 * Exercise templates that are shown at the top of the Exercise Library to
 * users who don't have any workouts logged.
 */
exports.popularExerciseTemplateIds = [
    'B8127AD1', // Lying Leg Curl (Machine)"
    '11A123F3', // Seated Leg Curl (Machine)"
    'D04AC939', // Squat (Barbell)"
    '75A4F6C4', // Leg Extension (Machine)"
    '422B08F1', // Lateral Raise (Dumbbell)"
    'C7973E0E', // Leg Press (Machine)"
    '07B38369', // Incline Bench Press (Dumbbell)"
    '3601968B', // Bench Press (Dumbbell)"
    '6A6C31A5', // Lat Pulldown (Cable)"
    '0393F233', // Seated Cable Row - V Grip (Cable)"
    '37FCC2BB', // Bicep Curl (Dumbbell)"
    '55E6546F', // Bent Over Row (Barbell)"
    '94B7239B', // Triceps Rope Pushdown"
    'C6272009', // Deadlift (Barbell)"
    '878CD1D0', // Shoulder Press (Dumbbell)"
    '651F844C', // Cable Fly Crossovers"
    '79D0BB3A', // Bench Press (Barbell)"
    '93A552C6', // Triceps Pushdown"
    '7E3BC8B6', // Hammer Curl (Dumbbell)"
    'BE640BA0', // Face Pull
];
